import { Pencil, ClipboardList } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateLead } from '../../hooks/use-leads';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

interface ObservationsProps {
  leadId: string;
  observations: string;
  canEdit: boolean;
}

export function Observations({
  leadId,
  observations,
  canEdit,
}: ObservationsProps): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateLead(leadId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(observations);

  useEffect(() => {
    setDraft(observations);
  }, [observations]);

  const empty = !observations.trim();

  return (
    <section className="rounded-md border border-emerald-200 bg-emerald-50/60 px-4 py-3">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-emerald-900">
          <ClipboardList className="h-4 w-4" />
          <span className="text-sm font-semibold">{t('notes.leadTitle')}</span>
        </div>
        {canEdit && !editing && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-emerald-900 hover:bg-emerald-100"
            onClick={() => setEditing(true)}
          >
            <Pencil className="h-3.5 w-3.5" />
            <span className="ml-1 text-xs">{t('notes.leadEditHint')}</span>
          </Button>
        )}
      </header>

      {editing ? (
        <div className="mt-2 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('notes.leadPlaceholder')}
            className="min-h-[110px] border-emerald-200 bg-white text-sm focus-visible:ring-emerald-400"
            maxLength={20_000}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraft(observations);
                setEditing(false);
              }}
              disabled={update.isPending}
            >
              {t('notes.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                await update.mutateAsync({ observations: draft });
                setEditing(false);
              }}
              disabled={update.isPending}
            >
              {update.isPending ? t('notes.saving') : t('notes.save')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-emerald-950/90">
          {empty ? (
            <span className="italic text-emerald-800/70">{t('notes.leadPlaceholder')}</span>
          ) : (
            observations
          )}
        </div>
      )}
    </section>
  );
}
